// Achievement definitions for the automation game

import { BuildingType, RESEARCHES, ResearchId } from '../constants';
import { secondsToTicks } from '../utils/TickIntervals';

import { Achievement, AchievementType, AchievementContext } from './types';

// Achievement checker function type
export type AchievementChecker = (context: AchievementContext) => boolean;

// Achievement definition with checker function
export interface AchievementDefinition
  extends Omit<Achievement, 'unlocked' | 'unlockedAt'> {
  check: AchievementChecker;
}

const POWER_HUNGRY_THRESHOLD_MW = 10;
const POWER_UPTIME_TARGET_TICKS = secondsToTicks(60 * 10);
const FLOW_STATE_MACHINE_THRESHOLD = 20;
const DATA_DRIP_THRESHOLD = 1000;
const RESEARCH_COMPLETION_TARGET = 10;
const ORE_EXTRACTION_TARGET = 10_000;
const IRON_PLATE_TARGET = 10_000;

const researchTierCache = new Map<string, number>();

const getResearchTier = (researchId: ResearchId): number => {
  const cached = researchTierCache.get(researchId);
  if (cached !== undefined) {
    return cached;
  }

  const research = RESEARCHES[researchId];
  if (!research) {
    researchTierCache.set(researchId, 0);
    return 0;
  }

  if (!research.prerequisites || research.prerequisites.length === 0) {
    researchTierCache.set(researchId, 1);
    return 1;
  }

  const tier =
    1 +
    Math.max(
      ...research.prerequisites.map(prereq =>
        getResearchTier(prereq as ResearchId)
      )
    );

  researchTierCache.set(researchId, tier);
  return tier;
};

// Achievement definitions
export const ACHIEVEMENT_DEFINITIONS: Record<
  AchievementType,
  AchievementDefinition
> = {
  [AchievementType.FIRST_GAME]: {
    id: AchievementType.FIRST_GAME,
    name: 'First Steps',
    description: 'Start your first automation game',
    icon: '🎮',
    check: (context: AchievementContext) => {
      // Unlocked when starting a new game (or loading for the first time)
      return context.isNewGame === true;
    },
  },

  [AchievementType.FIRST_MINER]: {
    id: AchievementType.FIRST_MINER,
    name: 'Miner Apprentice',
    description: 'Place your first miner on a resource node',
    icon: '⚒️',
    check: (context: AchievementContext) => {
      // Unlocked when placing a miner building
      return context.buildingPlaced?.buildingType === BuildingType.MINER;
    },
  },
  [AchievementType.FIRST_POWER]: {
    id: AchievementType.FIRST_POWER,
    name: 'Powering Up',
    description: 'Build your first generator to power the factory',
    icon: '🔌',
    check: (context: AchievementContext) => {
      // Unlocked when placing a generator building
      return context.buildingPlaced?.buildingType === BuildingType.GENERATOR;
    },
  },

  [AchievementType.FIRST_BELT]: {
    id: AchievementType.FIRST_BELT,
    name: 'Logistics Rookie',
    description: 'Lay down your first conveyor belt',
    icon: '📦',
    check: (context: AchievementContext) => {
      if (!context.buildingPlaced) {
        return false;
      }

      const beltTypes: BuildingType[] = [
        BuildingType.BELT,
        BuildingType.FAST_BELT,
        BuildingType.UNDERGROUND_BELT,
      ];

      return beltTypes.includes(
        context.buildingPlaced.buildingType as BuildingType
      );
    },
  },

  [AchievementType.FIRST_RESEARCH]: {
    id: AchievementType.FIRST_RESEARCH,
    name: 'Scientific Breakthrough',
    description: 'Complete your first research project',
    icon: '🔬',
    check: (context: AchievementContext) => {
      return Boolean(context.researchUnlocked?.researchId);
    },
  },

  [AchievementType.FIRST_FACTORY]: {
    id: AchievementType.FIRST_FACTORY,
    name: 'Industrial Pioneer',
    description: 'Construct your first factory to scale up production',
    icon: '🏭',
    check: (context: AchievementContext) => {
      return context.buildingPlaced?.buildingType === BuildingType.FACTORY;
    },
  },

  [AchievementType.POWER_HUNGRY]: {
    id: AchievementType.POWER_HUNGRY,
    name: 'Power Hungry',
    description: 'Exceed 10 MW of total power consumption.',
    icon: '⚡',
    check: (context: AchievementContext) => {
      return (context.powerDemand ?? 0) > POWER_HUNGRY_THRESHOLD_MW;
    },
  },

  [AchievementType.LIGHTS_STAY_ON]: {
    id: AchievementType.LIGHTS_STAY_ON,
    name: 'The Lights Stay On',
    description: 'Maintain 100% power uptime for 10 minutes.',
    icon: '💡',
    check: (context: AchievementContext) => {
      return (context.powerUptimeTicks ?? 0) >= POWER_UPTIME_TARGET_TICKS;
    },
  },

  [AchievementType.FLOW_STATE]: {
    id: AchievementType.FLOW_STATE,
    name: 'Flow State',
    description: 'Have 20 machines operating simultaneously',
    icon: '⚙️',
    check: (context: AchievementContext) => {
      return (context.powerActiveMachines ?? 0) >= FLOW_STATE_MACHINE_THRESHOLD;
    },
  },

  [AchievementType.DATA_DRIP]: {
    id: AchievementType.DATA_DRIP,
    name: 'Data Drip',
    description: 'Produce your first 1,000 research data units.',
    icon: '🧪',
    check: (context: AchievementContext) => {
      return (context.totalResearchDataProduced ?? 0) >= DATA_DRIP_THRESHOLD;
    },
  },

  [AchievementType.EUREKA]: {
    id: AchievementType.EUREKA,
    name: 'Eureka!',
    description: 'Unlock your first Tier 3 technology.',
    icon: '💥',
    check: (context: AchievementContext) => {
      const researchId = context.researchUnlocked?.researchId as
        | ResearchId
        | undefined;
      if (!researchId) {
        return false;
      }

      return getResearchTier(researchId) >= 3;
    },
  },

  [AchievementType.APPLIED_ENTROPY]: {
    id: AchievementType.APPLIED_ENTROPY,
    name: 'Applied Entropy',
    description: 'Complete 10 research topics',
    icon: '📚',
    check: (context: AchievementContext) => {
      return (
        (context.totalResearchCompleted ?? 0) >= RESEARCH_COMPLETION_TARGET
      );
    },
  },

  [AchievementType.RESOURCE_BARON]: {
    id: AchievementType.RESOURCE_BARON,
    name: 'Resource Baron',
    description: 'Extract 10,000 units of ore total.',
    icon: '⛏️',
    check: (context: AchievementContext) => {
      return (context.totalOreExtracted ?? 0) >= ORE_EXTRACTION_TARGET;
    },
  },

  [AchievementType.IRON_REVOLUTION]: {
    id: AchievementType.IRON_REVOLUTION,
    name: 'Iron Revolution',
    description: 'Produce 10,000 iron plates.',
    icon: '🏗️',
    check: (context: AchievementContext) => {
      return (context.totalIronPlatesProduced ?? 0) >= IRON_PLATE_TARGET;
    },
  },

  [AchievementType.OFFSITE_BACKUP]: {
    id: AchievementType.OFFSITE_BACKUP,
    name: 'Offsite Backup',
    description: 'Export a save',
    icon: '💾',
    check: (context: AchievementContext) => {
      return context.saveExported === true;
    },
  },
};

// Helper function to get all achievement definitions as an array
export const getAllAchievements = (): AchievementDefinition[] => {
  return Object.values(ACHIEVEMENT_DEFINITIONS);
};

// Helper function to get achievement definition by type
export const getAchievementDefinition = (
  type: AchievementType
): AchievementDefinition | undefined => {
  return ACHIEVEMENT_DEFINITIONS[type];
};

// Helper function to create initial achievement state (all locked)
export const createInitialAchievementState = (): Achievement[] => {
  return getAllAchievements().map(def => ({
    id: def.id,
    name: def.name,
    description: def.description,
    icon: def.icon,
    unlocked: false,
  }));
};
